import { Option, Schema } from 'effect';
import { optionalCatching } from '@/lib/search-params';
// Stale-chunk recovery: after a deploy, the CDN deletes hashed bundles that
// long-lived clients still reference, so a client-side navigation's dynamic
// `import()` 404s. Vite wraps that import in `__vitePreload`, which dispatches
// `vite:preloadError` and — per importAnalysisBuild.ts `handlePreloadError` —
// rethrows the error unless the event is `preventDefault()`ed.
//
// We deliberately DO NOT call `preventDefault()`. Doing so made `__vitePreload`
// resolve the import to `undefined`, so the generated `.then((d) => d.Route)`
// in routeTree.gen.ts threw `Cannot read properties of undefined (reading
// 'Route')` (Sentry WEB-BD). Letting Vite reject instead means
// `.then((d) => d.Route)` is skipped entirely — the WEB-BD throw is impossible
// — and the failure surfaces as the recognizable "Failed to fetch dynamically
// imported module" rejection, which a real route load routes through the
// router's `defaultOnCatch`.
//
// On any such failure we schedule a single (rate-limited) reload so the client
// re-fetches fresh HTML pointing at the current chunks. For the brief window
// while that reload navigates, the failed import's error/rejection is an
// expected artifact of the recovery, not a real fault — we suppress only that
// shape, only inside the window, so Sentry isn't spammed. Outside the window
// (rate-limited, or a reload that never navigated) nothing is suppressed, so
// persistent breakage still reaches Sentry.
//
// Both entry points feed `reloadForStaleChunk`: the `vite:preloadError`
// listener (catches `<link rel="modulepreload">` and intent-preload failures
// the router never sees) and `handleRouterStaleChunk` wired into the router's
// `defaultOnCatch` (catches route-level `.lazy()` load failures).
const RELOAD_STATE_KEY = 'stale_chunk_reload_state';
const MAX_RELOADS_PER_SESSION = 3;
const MIN_RELOAD_INTERVAL_MS = 10_000;
// How long after scheduling a reload we treat chunk errors as recovery noise.
// Long enough for the navigation to start, short enough that a reload that
// never navigates (blocked/stopped by the user) can't mask real failures for
// the rest of the page's lifetime.
const RELOAD_SUPPRESSION_WINDOW_MS = 10_000;

type ReloadState = { count: number; lastAt: number };

const decodeReloadState = Schema.decodeUnknownOption(
  Schema.fromJsonString(
    Schema.Struct({
      count: optionalCatching(Schema.Finite),
      lastAt: optionalCatching(Schema.Finite),
    }),
  ),
);

// Timestamp of the last reload we scheduled (0 = none this page load). A
// timestamp rather than a boolean so suppression self-expires without a timer.
let reloadScheduledAt = 0;

function readReloadState(): ReloadState {
  try {
    const raw = sessionStorage.getItem(RELOAD_STATE_KEY);
    if (!raw) return { count: 0, lastAt: 0 };
    return decodeReloadState(raw).pipe(
      Option.match({
        onNone: () => ({ count: 0, lastAt: 0 }),
        onSome: ({ count, lastAt }) => ({
          count: count ?? 0,
          lastAt: lastAt ?? 0,
        }),
      }),
    );
  } catch {
    return { count: 0, lastAt: 0 };
  }
}

export function reloadForStaleChunk(): boolean {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined')
    return false;
  const state = readReloadState();
  const now = Date.now();
  if (state.count >= MAX_RELOADS_PER_SESSION) return false;
  if (now - state.lastAt < MIN_RELOAD_INTERVAL_MS) return false;
  sessionStorage.setItem(
    RELOAD_STATE_KEY,
    JSON.stringify({ count: state.count + 1, lastAt: now }),
  );
  reloadScheduledAt = now;
  window.location.reload();
  return true;
}

// Mirrors @tanstack/router-core isModuleNotFoundError (not re-exported).
export function isModuleNotFoundError(error: Error): boolean {
  const m = error.message;
  return (
    m.startsWith('Failed to fetch dynamically imported module') ||
    m.startsWith('error loading dynamically imported module') ||
    m.startsWith('Importing a module script failed')
  );
}

// A failed-chunk error/rejection is expected noise ONLY within the navigation
// window of a reload we just scheduled. Outside it (rate-limited failure, or a
// reload that never fired) it must surface so persistent breakage stays visible.
export function shouldSuppressStaleChunkError(error: unknown): boolean {
  if (!(error instanceof Error) || !isModuleNotFoundError(error)) return false;
  return (
    reloadScheduledAt !== 0 &&
    Date.now() - reloadScheduledAt < RELOAD_SUPPRESSION_WINDOW_MS
  );
}

export function handleRouterStaleChunk(error: Error): void {
  if (isModuleNotFoundError(error)) reloadForStaleChunk();
}

// Test-only: control the suppression window. Pass 0 (default) to close it, or a
// timestamp to simulate a reload having just been scheduled — lets tests open
// the window without actually calling `window.location.reload()`.
export function setReloadScheduledAtForTest(at = 0): void {
  reloadScheduledAt = at;
}

if (typeof window !== 'undefined') {
  // Schedule a reload on a failed chunk preload. No `preventDefault()` — see the
  // module header for why letting Vite reject avoids the WEB-BD TypeError.
  window.addEventListener('vite:preloadError', () => {
    reloadForStaleChunk();
  });

  // Suppress the failed-chunk error/rejection while the recovery reload is
  // navigating, so it doesn't reach Sentry. Sentry's global handlers are the
  // `window.onerror` / `window.onunhandledrejection` *properties* (not
  // addEventListener), installed after these listeners. A capture-phase
  // listener that calls `stopImmediatePropagation()` runs first and prevents
  // those property handlers from firing at all — `preventDefault()` alone does
  // NOT (it only suppresses the browser's default console logging, not Sentry).
  const suppress = (event: Event, error: unknown) => {
    if (shouldSuppressStaleChunkError(error)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  };
  window.addEventListener(
    'error',
    (event) => suppress(event, event.error),
    true,
  );
  window.addEventListener(
    'unhandledrejection',
    (event) => suppress(event, event.reason),
    true,
  );
}
